import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Container, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Products.css";

function formatText(text) {
  if (typeof text !== "string") return text;
  return text.split("\n").map((line, i, arr) => (
    <React.Fragment key={i}>
      {line}
      {i < arr.length - 1 && <br />}
    </React.Fragment>
  ));
}

const SECTIONS = [
  {
    id: "pioneer",
    enTitle: "Pioneer",
    jaTitle: "ウェーハ洗浄装置のパイオニア",
    lead: "高精度・高効率の洗浄を実現する",
    text: "各種ウェーハの洗浄において高精度・高効率を実現する洗浄機です。\n酸・アルカリなど様々な薬液に対応。",
    slides: [
      { img: "/images/product.png",  caption: "高精度バッチ洗浄システム" },
    ],
    reverse: false,
    index: 0,
  },
  {
    id: "custom",
    enTitle: "Customization",
    jaTitle: "柔軟なカスタマイズ",
    lead: "お客様のニーズに完全対応",
    text: "お客様のご要望に応じて、機能・仕様を柔軟にカスタマイズ可能です。\n省スペース、変則的な配置など弊社にお任せください",
    slides: [
      { img: "/images/product2.png", caption: "柔軟なカスタマイズ" },
    ],
    reverse: true,
    index: 1,
  },
  {
    id: "expert",
    enTitle: "Expertise",
    jaTitle: "豊富な知識・経験によるアドバイス",
    lead: "60年超の半導体洗浄ノウハウ",
    text: "長年の半導体洗浄の知見から、最適なプロセスや製品構造などのアドバイスを提供します",
    slides: [
      { img: "/images/product3.png", caption: "設計・支援" },
    ],
    reverse: false,
    index: 2,
  },
];

/* ── サムネイルスライダー ── */
function SlideGallery({ slides }) {
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);

  const goTo = useCallback((i) => {
    if (fading || i === active) return;
    setFading(true);
    setTimeout(() => { setActive(i); setFading(false); }, 320);
  }, [active, fading]);

  return (
    <div className="gallery">
      <div className={`gallery-main${fading ? " fading" : ""}`}>
        <img src={slides[active].img} alt={slides[active].caption} className="gallery-img" />
        <div className="gallery-caption">{slides[active].caption}</div>
        <div className="gallery-corner tl" /><div className="gallery-corner br" />
      </div>
      <div className="gallery-thumbs">
        {slides.map((s, i) => (
          <button key={i} className={`gallery-thumb${i === active ? " active" : ""}`} onClick={() => goTo(i)}>
            <img src={s.img} alt={s.caption} />
            <span className="thumb-caption">{s.caption}</span>
            <span className="thumb-progress" />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── カウントアップ ── */
function useCountUp(target, duration, go) {
  const [v, setV] = useState("0");
  useEffect(() => {
    if (!go) return;
    const n = parseFloat(target);
    const sfx = target.replace(/[\d.]/g, "");
    if (isNaN(n)) { setV(target); return; }
    let t0 = null;
    const tick = (ts) => {
      if (!t0) t0 = ts;
      const p = Math.min((ts - t0) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setV((e * n).toFixed(target.includes(".") ? 1 : 0) + sfx);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [go, target, duration]);
  return v;
}

function StatItem({ value, unit, label }) {
  const ref = useRef(null);
  const [go, setGo] = useState(false);
  const v = useCountUp(value, 1600, go);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setGo(true); obs.disconnect(); } }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className="stat-item">
      <div className="stat-num">{go ? v : "0"}<span className="stat-unit">{unit}</span></div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

/* ── メイン ── */
export default function ProductPage() {
  const [scrollY, setScrollY] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const tickRef = useRef(false);

  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  useEffect(() => {
    const h = () => {
      if (tickRef.current) return;
      tickRef.current = true;
      requestAnimationFrame(() => { setScrollY(window.scrollY); tickRef.current = false; });
    };
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("is-visible"); obs.unobserve(e.target); } });
    }, { threshold: 0.12 });
    document.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const videoY   = useMemo(() => Math.min(scrollY * 0.35, 110), [scrollY]);
  const contentO = useMemo(() => Math.max(1 - scrollY / 460, 0), [scrollY]);
  const contentY = useMemo(() => Math.min(scrollY * 0.16, 60),   [scrollY]);

  return (
    <div className="pp">
      <div id="page-top" />

      {/* ────── Products-hero ────── */}
      <section className={`pp-Products-hero${loaded ? " loaded" : ""}`}>
        <div className="Products-hero-media">
          <video className="Products-hero-video" autoPlay loop muted playsInline disablePictureInPicture
            style={{ transform: `translate(-50%, calc(-50% + ${videoY}px))` }}>
            <source src="/videos/Products-hero.mp4" type="video/mp4" />
          </video>
          <div className="Products-hero-grad" />
        </div>

        <div className="Products-hero-body" style={{ opacity: contentO, transform: `translateY(${contentY}px)` }}>
          <p className="Products-hero-eyebrow">ZENKYO PRECISION TECHNOLOGY</p>
          <h1 className="Products-hero-h1">
            <span className="hl hl1">パーティクル</span>
            <span className="hl hl2 accent">ZERO</span>
            <span className="hl hl3">を目指して</span>
          </h1>
          <p className="Products-hero-sub">高精度・高効率の洗浄機でお客様の課題をサポートします。</p>
          <Button as={Link} to="/contact#page-top" className="Products-hero-btn">
            お問い合わせはこちら <span aria-hidden="true">→</span>
          </Button>
        </div>

        <div className="Products-hero-scroll" aria-hidden="true">
          <div className="scroll-bar"><div className="scroll-nub" /></div>
          <span>SCROLL</span>
        </div>
        <p className="Products-hero-side" aria-hidden="true">SEMICONDUCTOR CLEANING SPECIALIST</p>
      </section>

      {/* ────── STATS ────── */}
      <div className="pp-stats reveal">
        <Container>
          <div className="stats-grid">
            <StatItem value="99.9" unit="%" label="お客様満足度" />
            <div className="stats-div" />
            <StatItem value="60"   unit="+" label="年の実績" />
            <div className="stats-div" />
            <StatItem value="1400"  unit="台" label="導入実績" />
          </div>
        </Container>
      </div>

      {/* ────── SECTIONS ────── */}
      {SECTIONS.map((sec) => (
        <section key={sec.id} id={sec.id} className={`pp-sec${sec.index % 2 === 1 ? " alt" : ""}`}>
          <Container>
            <div className={`pp-sec-inner${sec.reverse ? " rev" : ""}`}>
              <div className="pp-sec-media reveal">
                <SlideGallery slides={sec.slides} />
              </div>
              <div className="pp-sec-text reveal">
                <p className="sec-en">{sec.enTitle}</p>
                <h2 className="sec-ja">{sec.jaTitle}</h2>
                <p className="sec-lead">{sec.lead}</p>
                <hr className="sec-rule" />
                <p className="sec-body">{formatText(sec.text)}</p>
                <Link to="/contact#page-top" className="sec-cta">
                  詳細を問い合わせる <span className="sec-cta-arrow" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </Container>
          <span className="sec-num" aria-hidden="true">{String(sec.index + 1).padStart(2, "0")}</span>
        </section>
      ))}

      {/* ────── FINAL CTA ────── */}
      <section className="pp-cta reveal">
        <div className="cta-stripe" aria-hidden="true" />
        <Container>
          <p className="cta-en">CONTACT US</p>
          <h3 className="cta-ja">半導体洗浄のことなら<br />なんでもご相談ください</h3>
          <Button as={Link} to="/contact#page-top" className="Products-hero-btn">
            お問い合わせはこちら <span aria-hidden="true">→</span>
          </Button>
        </Container>
      </section>
    </div>
  );
}
